/* eslint-disable camelcase */
import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { ReactSVG } from 'react-svg';
import { withStyles } from '@glu/theming';
import Spinner from 'common/Spinner';
import Button from 'common/Button';
import ErrorMsg from 'common/ErrorMsg';
import { withFieldProps } from 'hooks/useFieldProps';
import mobilePhoneIcon from 'icons/mobile-phone.svg';
import keyboardIcon from 'icons/keyboard.svg';
import pageActions from 'core/page/actions';
import isMobileDevice from 'utils/isMobileDevice';
import { useSelector } from 'react-redux';
import { runValidators } from 'core/fields/sagas';
import fieldsActions from 'core/fields/actions';
import String from './String';

const styles = ({ links, palette, spacing, typography }) => ({
  button: {
    '& > i.spinner': {
      position: 'relative',
      right: 0,
      top: 0,
    },
    '& svg': {
      fill: 'transparent',
      fillOpacity: 0,
      stroke: palette.icon,
    },
    alignItems: 'center',
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    display: 'flex',
    margin: 0,
    padding: 5,
    position: 'relative',
  },
  inputRoot: {
    '& .form-group': {
      '& input': {
        marginTop: 0,
      },
      margin: 0,
    },

    width: 210,
  },
  inputWrap: {
    '& > i.spinner': {
      position: 'relative',
      right: -15,
      top: 12,
    },
    alignItems: 'flex-start',
    display: 'flex',
  },
  label: {
    marginBottom: spacing.md,
  },
  option: {
    '& h3': {
      marginBottom: spacing.unit,
    },
    '&:first-child': {
      marginBottom: spacing.sm,
    },
  },
  optionLabel: {
    ...typography.p2,
    color: links.link.color,
    fontWeight: typography.fontWeights.bold,
    margin: 0,
    marginLeft: spacing.unit,
    padding: 0,
  },
  root: {
    '&.loading': {
      cursor: 'not-allowed',
      opacity: 0.5,
      pointerEvents: 'none',
    },
    marginTop: 32,
    position: 'relative',
  },
  sendButton: {
    ...typography.p2,
    background: 'none',
    border: 'none',
    color: links.link.color,
    cursor: 'pointer',
    fontWeight: typography.fontWeights.bold,
    lineHeight: '42px',
    margin: 0,
    marginLeft: spacing.sm,
    outline: 'none',
    padding: 0,
  },
  support: {
    ...typography.p3,
    marginBottom: spacing.xs,
  },
});

const Verification = ({
  classes,
  error,
  groupName,
  label,
  loading,
  mobilePhoneField,
  mobileVerificationSendLabel,
  name,
  onChange,
  onNext,
  onReRender,
  options,
  value,
}) => {
  const [isMobileNumberInvalid, setIsMobileNumberInvalid] = useState(true);

  const mField = useSelector(({ fields }) => fields[mobilePhoneField]);
  const mValue = useSelector(({ data }) => data[mobilePhoneField]);
  const isMobileVerification = useSelector(({ data }) => data[name] === 'mobilePhone');

  const renderVerificationOptions = (option) => {
    const isMobilePhone = option.type === 'mobilePhone';
    if (loading && value === option.type) {
      return <Spinner loading />;
    }
    if (isMobileVerification && isMobilePhone) {
      return (
        <div className={classes.inputWrap}>
          <div className={classes.inputRoot}>
            <String {...mField} name={mobilePhoneField} groupName={groupName} />
          </div>
          {loading && value === option.type ? (
            <Spinner loading />
          ) : (
            <Button
              variant="tertiary"
              className={classes.sendButton}
              disabled={isMobileNumberInvalid || error}
              onClick={onNext}
            >
              {mobileVerificationSendLabel}
            </Button>
          )}
        </div>
      );
    }
    return (
      <>
        <button
          type="button"
          className={classes.button}
          onClick={(e) => {
            e.preventDefault();
            onChange(name, option.type);
            if (isMobilePhone && !isMobileDevice()) {
              onReRender(name);
            } else {
              onNext();
            }
          }}
        >
          <ReactSVG src={isMobilePhone ? mobilePhoneIcon : keyboardIcon} />
          <span className={classes.optionLabel}>{option.label}</span>
        </button>
      </>
    );
  };

  useEffect(() => {
    if (isMobileVerification) {
      setIsMobileNumberInvalid(mValue ? !!runValidators(mField.validators, mValue) : true);
    }
  }, [isMobileVerification, mField.validators, mValue]);

  return (
    <div className={classnames('form-group', { error, loading }, classes.root)}>
      <h2 className={classes.label}>{label}</h2>
      <div>
        {options?.map((option) => (
          <div className={classes.option} key={option.type}>
            <h3>{option.title}</h3>
            <p className={classes.support}>{option.helpText}</p>
            {renderVerificationOptions(option)}
          </div>
        ))}
      </div>
      <ErrorMsg name={name} />
    </div>
  );
};

Verification.propTypes = {
  error: PropTypes.bool.isRequired,
  groupName: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  loading: PropTypes.bool.isRequired,
  mobilePhoneField: PropTypes.string.isRequired,
  mobileVerificationSendLabel: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  onNext: PropTypes.func.isRequired,
  onReRender: PropTypes.func.isRequired,
  options: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  value: PropTypes.string,
};

Verification.defaultProps = {
  value: '',
};

const VerificationStyled = withStyles(styles)(Verification);

const mapStateToProps = ({ fields, resources, ui }, { name }) => {
  const field = fields[name];
  return {
    error: fields[field.mobilePhoneField].error,
    loading: ui.appLoading || ui.pageLoading || ui.groupLoading || false,
    mobilePhoneField: field.mobilePhoneField,
    mobileVerificationSendLabel: resources.mobileVerificationSendLabel,
    options: field.options,
  };
};

const mapDispatchToProps = (dispatch) => ({
  onNext: () => {
    dispatch(pageActions.next());
  },
  onReRender: (name) => {
    dispatch(fieldsActions.reRender(name));
  },
});

export default withFieldProps(mapStateToProps, mapDispatchToProps)(VerificationStyled);
